import dayjs from "dayjs";
import mapValues from "lodash/mapValues";
import styled from "styled-components";

import type { AdIntelligenceRecordsSummaryRecord } from "@similarweb/pro-api-client";
import { shortMonthAndYear, Table } from "@similarweb/pro-logic-components";
import type { ColumnsType } from "@similarweb/pro-logic-components";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import { Card, ChangeValue, Tooltip } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { chosenSites as chosenSitesService } from "app/common/services/chosenSites";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter, minimumValueChangeFilter } from "app/filters/ngFilters";
import { useDisplayAdvertiserPublisherDataAndParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useDisplayAdvertiserPublisherDataAndParams";
import type { AdIntelligenceController } from "app/pages/digital-marketing/ad-intelligence/common/shared/types/types";
import DurationService from "app/services/DurationService";
import type { dateForTooltip } from "app/services/DurationService";
import { allTrackers } from "app/services/track/track";
import { InfoIcon } from "pro-features/components/BoxTitle/src/BoxTitle";
import { LeftFlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import { TOP_CAMPAIGNS_TABLE_ID } from "../../../advertisers/overview/components/RecentCampaignsTable";
import { QueryBarItemImageStyled } from "../../../advertisers/overview/components/styled";
import { TABLE_ID as TOP_ADVERTISERS_PUBLISHERS_TABLE_ID } from "../../advertisersPublishers/TopAdvertisersPublishers";
import { NA_VALUE } from "../../utils/utils";
import { useActivityBreakdownCompare } from "../hooks/useActivityBreakdownCompare";
import type { ActivityBreakdownData } from "../hooks/useActivityBreakdownCompare";
import { elementIdToEventNameMap } from "../PerformanceEntitiesWidget";
import { TOP_ADS_TABLE_ID } from "../RecentAdsGallery";

const activityToElementIdMap: Record<string, string> = {
    campaigns: TOP_CAMPAIGNS_TABLE_ID,
    creatives: TOP_ADS_TABLE_ID,
    publishers: TOP_ADVERTISERS_PUBLISHERS_TABLE_ID,
    advertisers: TOP_ADVERTISERS_PUBLISHERS_TABLE_ID,
};

const SubLabel = styled.div`
    ${setFontVariant("queryBar1", colorsPalettes.carbon[300])};
    margin-top: 4px;
    margin-bottom: 4px;
`;

const Count = styled.span`
    ${setFontVariant("h5", colorsPalettes.carbon[400])};
`;

const ClickableActivityCell = styled(Flex)`
    cursor: pointer;
    &:hover {
        color: ${colorsPalettes.blue[500]};
    }
`;

const getSubLabelText = (
    loading: boolean,
    startDate: string | undefined,
    durationForTooltip: dateForTooltip,
): string | null => {
    const shouldShowSubLabel = !loading && !!startDate;
    if (!shouldShowSubLabel || !startDate) {
        return null;
    }

    const startDateFormatted = dayjs(startDate).format(shortMonthAndYear);
    return startDateFormatted === durationForTooltip.to
        ? startDateFormatted
        : `${startDateFormatted} - ${durationForTooltip.to}`;
};

const isDataEmpty = (data: ActivityBreakdownData | undefined): boolean => {
    if (!data?.data?.length) {
        return true;
    }

    const hasAnyNonZeroCount = data.data.some((row) => {
        const domainEntries = Object.entries(row).filter(([key]) => key !== "activity");

        return domainEntries.some(([, dataPoint]) => {
            if ((dataPoint as AdIntelligenceRecordsSummaryRecord | undefined)?.count) {
                return true;
            }
            return false;
        });
    });

    return !hasAnyNonZeroCount;
};

const scrollIntoView = (elementId: string) => {
    const element = document.getElementById(elementId);
    const pageElement = document.getElementsByClassName("sw-layout-scrollable-element")?.[0];
    if (element && pageElement) {
        allTrackers.dispatchTrackEvent("Internal Link", "click", elementIdToEventNameMap[elementId]);
        pageElement.scroll({ top: element.offsetTop, behavior: "smooth" });
    }
};

const getElementIdForActivity = (activity: string, controller: AdIntelligenceController): string | null => {
    if (controller === "advertiser" || activity === "publishers" || activity === "advertisers") {
        return activityToElementIdMap[activity] ?? null;
    }
    return null;
};

const generateColumnConfig = (
    domains: string[],
    favicons: Record<string, string>,
    controller: AdIntelligenceController,
    durationForTooltip: dateForTooltip,
    isWindow: boolean,
    shouldShowSubLabel: boolean,
    subLabelText: string | null,
): ColumnsType => [
    {
        dataIndex: "activity",
        title: () => translate("ad.intelligence.advertiser.overview.activity.breakdown.compare.table.activity.label"),
        width: 140,
        render: (activity: string) => {
            const translationKey = `ad.intelligence.${controller}.overview.activity.breakdown.compare.table.${activity}.label`;
            const tooltipKey = `ad.intelligence.${controller}.overview.performance.entities.widget.${activity}.tooltip`;
            const showSubLabelForRow = activity !== "publishers" && activity !== "advertisers";
            const elementId = getElementIdForActivity(activity, controller);

            const handleClick = () => {
                if (elementId) {
                    scrollIntoView(elementId);
                }
            };

            return (
                <ClickableActivityCell
                    onClick={handleClick}
                    flexDirection="column"
                    height="45px"
                    justifyContent="center"
                >
                    <LeftFlexRow style={{ columnGap: "4px" }}>
                        {translate(translationKey)}
                        <Tooltip content={translate(tooltipKey)}>
                            <span>
                                <InfoIcon iconName="info" />
                            </span>
                        </Tooltip>
                    </LeftFlexRow>
                    {shouldShowSubLabel && showSubLabelForRow ? <SubLabel>{subLabelText}</SubLabel> : null}
                </ClickableActivityCell>
            );
        },
    },
    ...(domains.map((domain) => ({
        dataIndex: domain,
        title: () => (
            <QueryBarItemImageStyled
                image={favicons[domain]}
                borderColor={colorsPalettes.carbon["50"]}
                badgeColor={chosenSitesService.getSiteColor(domain)}
            />
        ),
        align: "center",
        width: 148,
        tooltipTitle: domain,
        render: (dataPoint: { count: number; change?: number }) => {
            return (
                <Flex
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Count data-automation-count={dataPoint.count}>
                        {dataPoint.count ? abbrNumberFilter()(dataPoint.count) : NA_VALUE}
                    </Count>
                    {!dataPoint.change ? (
                        NA_VALUE
                    ) : (
                        <Tooltip
                            content={
                                isWindow
                                    ? translate("ad.intelligence.overview.performance.widget.window.change.tooltip")
                                    : translate(
                                          "ad.intelligence.overview.performance.entities.widget.change.tooltip",
                                          durationForTooltip,
                                      )
                            }
                        >
                            <span>
                                <ChangeValue
                                    value={
                                        !dataPoint.change ? NA_VALUE : minimumValueChangeFilter()(dataPoint.change, 1)
                                    }
                                    isDecrease={dataPoint.change < 0}
                                />
                            </span>
                        </Tooltip>
                    )}
                </Flex>
            );
        },
    })) as ColumnsType),
];

interface IActivityBreakdownCompare {
    controller?: AdIntelligenceController;
}

export const ActivityBreakdownCompare = ({ controller = "advertiser" }: IActivityBreakdownCompare) => {
    const subtitleFilters = useSubtitleFilters("date", "country", "webSource");
    const { data: activityBreakdownData, loading, error } = useActivityBreakdownCompare(controller);
    const params = useDisplayAdvertiserPublisherDataAndParams();
    const { duration } = params;
    const { isWindow } = DurationService.getDurationData(duration).forAPI;

    const durationForTooltip = mapValues(
        DurationService.getDurationData(duration).forTooltip as dateForTooltip,
        (item) => {
            return decodeURIComponent(item);
        },
    ) as dateForTooltip;

    const subLabelText = getSubLabelText(loading, activityBreakdownData?.startDate, durationForTooltip);
    const shouldShowSubLabel = !!subLabelText;

    const noDataObject = error
        ? {
              title: translate(`ad.intelligence.${controller}.table.error.title`),
              subtitle: translate(`ad.intelligence.${controller}.table.error.subtitle`),
          }
        : {
              title: translate(`ad.intelligence.${controller}.table.no.data.title`),
              subtitle: translate(`ad.intelligence.${controller}.table.no.data.subtitle`),
          };

    const hasEmptyData = isDataEmpty(activityBreakdownData);
    const tableData = hasEmptyData ? undefined : activityBreakdownData?.data;

    const columnConfig =
        activityBreakdownData?.domains && activityBreakdownData?.favicons
            ? generateColumnConfig(
                  activityBreakdownData.domains,
                  activityBreakdownData.favicons,
                  controller,
                  durationForTooltip,
                  isWindow,
                  shouldShowSubLabel,
                  subLabelText,
              )
            : [];

    return (
        <Card
            background={colorsPalettes.carbon[0]}
            margin="0 0 24px"
        >
            <Table
                data={tableData}
                columnConfig={columnConfig}
                theme="white"
                bordersConfig={{ body: true, header: true }}
                isLoading={loading}
                loaderRowCount={4}
                noData={noDataObject}
                withArrowButtons={false}
                size="small"
                scroll={{ x: "max-content" }}
            >
                <Table.TableTop borderBottom>
                    <Table.TableTop.Title
                        title={translate(
                            `ad.intelligence.${controller}.overview.activity.breakdown.compare.table.title`,
                        )}
                        filters={subtitleFilters}
                    />
                </Table.TableTop>
            </Table>
        </Card>
    );
};
